/**
 * PanelPlanning clipboard mixin — Ctrl+드래그 복사 + (선택) Ctrl+C/V 붙여넣기
 *
 * Ctrl+C/V: mixin `setupCopyPasteListeners()` 또는 optional 플러그인 attach.
 * 기본 `initPanelMapPlugins()`에는 키보드 플러그인 미포함.
 */
import Collection from 'ol/Collection';
import Translate from 'ol/interaction/Translate';
import { Polygon } from 'ol/geom';
import Feature from 'ol/Feature';
import siteDesignApi from '../../../apis/panel/site_design_api.js';
import { toPanelMapModuleFeatureId } from '../utils/panelMapFeatureId.js';
import {
  applyApiFieldsToModuleColRow as setModuleColRowOnVm,
  moduleColRowToApiFields
} from '../utils/panelDivisionGrid.js';
import { resolveNextStringDisplayIdInArray, logStringNumberingInArrayDebug } from '../utils/panelEntityNumbering.js';
import { resolveStringSeqFromMapFeature } from '../utils/panelMapSelection.js';
import {
  readStringDisplayNo,
  readStringSeq,
  writeStringIdentityOnFeature
} from '../utils/panelMapFeatureProps.js';
import {
  logStringCopySave,
  summarizeRawApiResult,
  traceStringCopySaveAsync
} from '../utils/stringCopySaveDebug.js';
import { resolveStringCopySkipGridSlots } from '../utils/panelStringCopySkipSlots.js';
import {
  panelPointerMovePerfBeginHandler,
  panelPointerMovePerfEndHandler
} from '../utils/panelPointerMovePerfLog.js';

export const clipSetup = {
  data() {
    return {
      copiedFeature: null,
      copiedFeatureType: null,
      isPasteMode: false,
      ctrlKeyPressed: false,
      /** Ctrl+드래그 진행 중이면 true — 클릭 선택과 분리 */
      _mapCtrlDragActive: false
    };
  },

  methods: {
    setupCopyPasteListeners() {
      if (this._copyPasteListenersBound) {
        return;
      }
      this._copyPasteListenersBound = true;
      this.copyPasteKeyHandler = event => {
        if (event.key === 'Control' || event.key === 'Meta') {
          this.ctrlKeyPressed = event.type === 'keydown';
          return;
        }
        if ((event.ctrlKey || event.metaKey) && event.key === 'c') {
          event.preventDefault();
          this.copySelectedFeature();
        } else if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
          event.preventDefault();
          this.activatePasteMode();
        } else if (event.key === 'Escape') {
          if (this._mapCtrlDragActive) {
            return;
          }
          if (this.cancelActiveTranslate?.()) {
            event.preventDefault();
            return;
          }
          if (this.isPasteMode) {
            event.preventDefault();
            this.deactivatePasteMode();
            this.syncSelectionHighlightFromCurrentSelection?.();
          }
        }
      };
      this.copyPasteKeyUpHandler = event => {
        if (event.key === 'Control' || event.key === 'Meta') {
          this.ctrlKeyPressed = false;
        }
      };
      this.copyPasteBlurHandler = () => {
        this.ctrlKeyPressed = false;
        if (this._mapCtrlDragActive || this.isPasteMode) {
          return;
        }
        this.restoreMainMapInteractionsAfterCtrlDrag?.();
        this.dragPanInteraction?.setActive?.(true);
      };
      document.addEventListener('keydown', this.copyPasteKeyHandler);
      document.addEventListener('keyup', this.copyPasteKeyUpHandler);
      window.addEventListener('blur', this.copyPasteBlurHandler);
    },

    teardownCopyPasteListeners() {
      if (this.copyPasteKeyHandler) {
        document.removeEventListener('keydown', this.copyPasteKeyHandler);
      }
      if (this.copyPasteKeyUpHandler) {
        document.removeEventListener('keyup', this.copyPasteKeyUpHandler);
      }
      if (this.copyPasteBlurHandler) {
        window.removeEventListener('blur', this.copyPasteBlurHandler);
      }
      this.deactivatePasteMode();
      this._copyPasteListenersBound = false;
    },

    activatePasteMode() {
      if (!this.copiedFeature) {
        console.warn('복사된 피처가 없습니다.');
        return;
      }
      this.isPasteMode = true;
      this.bindPastePlacementPreview();
      const mapElement = document.getElementById('map');
      if (mapElement) mapElement.style.cursor = 'crosshair';
    },

    deactivatePasteMode() {
      this.isPasteMode = false;
      this.unbindPastePlacementPreview();
      this.clearStringPlacementPreview?.();
      const mapElement = document.getElementById('map');
      if (mapElement) mapElement.style.cursor = '';
    },

    getCopiedStringSourceArraySeq() {
      if (this.copiedFeatureType === 'string') {
        return (
          this.copiedFeature?.string?.solarArraySeq ??
          this.copiedFeature?.string?.solarArrayId ??
          null
        );
      }
      if (this.copiedFeatureType === 'multistring') {
        const first = this.copiedFeature?.strings?.[0]?.string;
        return first?.solarArraySeq ?? first?.solarArrayId ?? null;
      }
      return null;
    },

    bindPastePlacementPreview() {
      if (this._pastePreviewMoveBound || !this.map) return;
      const viewport = this.map.getViewport();
      if (!viewport) return;

      this._pastePreviewMoveHandler = event => {
        const perf = panelPointerMovePerfBeginHandler('paste-preview');
        if (!this.isPasteMode) {
          panelPointerMovePerfEndHandler(perf, { skipped: 'not-paste-mode' });
          return;
        }
        if (this.copiedFeatureType !== 'string' && this.copiedFeatureType !== 'multistring') {
          panelPointerMovePerfEndHandler(perf, { skipped: 'not-string-copy' });
          return;
        }
        const coord = this.map.getCoordinateFromPixel(this.map.getEventPixel(event));
        this.updateStringPlacementPreview?.({
          dropCoordinate: coord,
          sourceArraySeq: this.getCopiedStringSourceArraySeq()
        });
        panelPointerMovePerfEndHandler(perf, { active: true });
      };
      viewport.addEventListener('pointermove', this._pastePreviewMoveHandler);
      this._pastePreviewMoveBound = true;
    },

    unbindPastePlacementPreview() {
      if (!this._pastePreviewMoveBound || !this._pastePreviewMoveHandler) return;
      this.map?.getViewport()?.removeEventListener('pointermove', this._pastePreviewMoveHandler);
      this._pastePreviewMoveHandler = null;
      this._pastePreviewMoveBound = false;
    },

    resolveCtrlDragSourceFeature(hit) {
      if (!hit) {
        return null;
      }
      const ft = (hit.get('featureType') || '').toLowerCase();
      if (ft !== 'module') {
        return hit;
      }
      const parent = this.vectorSource
        ?.getFeatures()
        .find(
          f =>
            (f.get('featureType') || '').toLowerCase() === 'string' &&
            readStringSeq(f) === readStringSeq(hit)
        );
      return parent || hit;
    },

    suspendMainMapInteractionsForCtrlDrag() {
      if (this.translateInteraction) {
        this.map.removeInteraction(this.translateInteraction);
      }
      if (this.dragPanInteraction) {
        this.map.removeInteraction(this.dragPanInteraction);
      }
    },

    restoreMainMapInteractionsAfterCtrlDrag() {
      if (!this.map) return;
      if (typeof this.updateModifyTranslateFeatures === 'function') {
        this.updateModifyTranslateFeatures();
      }
      // DragPan이 제거되었으면 다시 추가
      if (
        this.dragPanInteraction &&
        !this.map.getInteractions().getArray().includes(this.dragPanInteraction)
      ) {
        this.map.addInteraction(this.dragPanInteraction);
      }
    },

    teardownCtrlDragTranslate(state, { removeClones = true } = {}) {
      if (state.translateEndHandler && state.ctrlTranslate) {
        state.ctrlTranslate.un('translateend', state.translateEndHandler);
      }
      if (state.ctrlTranslate) {
        this.map.removeInteraction(state.ctrlTranslate);
        state.ctrlTranslate = null;
      }
      if (removeClones && this.vectorSource && state.cloneCollection) {
        state.cloneCollection.getArray().forEach(f => this.vectorSource.removeFeature(f));
      }
      state.original = null;
      state.clone = null;
      state.moduleClones = [];
      state.cloneCollection = null;
      state.translateEndHandler = null;
      state.handled = false;
      this._mapCtrlDragActive = false;
      this.restoreMainMapInteractionsAfterCtrlDrag();
    },

    resetCtrlDragState(state, options) {
      this.teardownCtrlDragTranslate(state, {
        removeClones: options?.removeClone !== false
      });
    },

    /** API rowNum/colNum → moduleRowNum / moduleColNum (필드명 1:1) */
    applyApiRowColToModuleColRow(rowNum, colNum) {
      setModuleColRowOnVm(this, rowNum, colNum);
    },

    resolveSourceStringRowCol(originalFeature) {
      const fromFeatureRow = originalFeature?.get?.('rowNum');
      const fromFeatureCol = originalFeature?.get?.('colNum');
      if (fromFeatureRow != null && fromFeatureCol != null) {
        return { rowNum: fromFeatureRow, colNum: fromFeatureCol };
      }

      const stringSeq = readStringSeq(originalFeature);
      if (stringSeq == null) {
        return { rowNum: null, colNum: null };
      }

      const fromFlattenedBySeq = this.flattenedSiteDesign?.byString?.get(Number(stringSeq))
        ?? this.flattenedSiteDesign?.byString?.get(stringSeq);
      if (fromFlattenedBySeq?.rowNum != null && fromFlattenedBySeq?.colNum != null) {
        return {
          rowNum: fromFlattenedBySeq.rowNum,
          colNum: fromFlattenedBySeq.colNum
        };
      }

      const stringDisplayNo = readStringDisplayNo(originalFeature);
      const fromSolarStrings = (this.solarStrings || []).find(
        s => String(s.stringId) === String(stringDisplayNo)
      );
      if (fromSolarStrings?.rowNum != null && fromSolarStrings?.colNum != null) {
        return {
          rowNum: fromSolarStrings.rowNum,
          colNum: fromSolarStrings.colNum
        };
      }

      const fromFlattened = this.flattenedSiteDesign?.byString
        ? [...this.flattenedSiteDesign.byString.values()].find(
            s =>
              String(s.stringId) === String(stringDisplayNo) ||
              String(s.displayNo) === String(stringDisplayNo)
          )
        : null;
      if (fromFlattened?.rowNum != null && fromFlattened?.colNum != null) {
        return {
          rowNum: fromFlattened.rowNum,
          colNum: fromFlattened.colNum
        };
      }

      return { rowNum: null, colNum: null };
    },

    /** Ctrl+드래그 복사 — 격자에서 삭제된 Module 칸(localIndex) */
    resolveCtrlDragCopySkipSlots(sourceStringFeature) {
      const { rowNum, colNum } = this.resolveSourceStringRowCol(sourceStringFeature);
      if (!rowNum || !colNum) {
        return null;
      }
      return resolveStringCopySkipGridSlots({
        sourceStringFeature,
        moduleRowNum: rowNum,
        moduleColNum: colNum,
        moduleNumberingOrientation: this.moduleNumberingOrientation,
        mapFeatures: this.vectorSource?.getFeatures?.() ?? []
      });
    },

    /**
     * Shift 다중 선택 후 Ctrl+드래그 — 선택된 String feature 목록
     * groupSelection 앵커가 String/Module 혼재여도 solarStringSeq 기준으로 String feature 수집
     */
    getSelectedStringFeaturesForCtrlDrag(hit) {
      const resolved = this.resolveCtrlDragSourceFeature(hit);
      if (!resolved) {
        return [];
      }

      const hitStringSeq = resolveStringSeqFromMapFeature(resolved);
      if (!hitStringSeq) {
        return [];
      }

      const resolveStringFeatureBySeq = stringSeq => {
        const fromSelection = (this.groupSelection?.getArray() ?? []).find(
          f =>
            (f.get('featureType') || '').toLowerCase() === 'string' &&
            resolveStringSeqFromMapFeature(f) === stringSeq
        );
        if (fromSelection) {
          return fromSelection;
        }
        return this.vectorSource?.getFeatures().find(
          f =>
            (f.get('featureType') || '').toLowerCase() === 'string' &&
            resolveStringSeqFromMapFeature(f) === stringSeq
        );
      };

      const selectedStringSeqs = new Set();
      (this.groupSelection?.getArray() ?? []).forEach(f => {
        const seq = resolveStringSeqFromMapFeature(f);
        if (seq) {
          selectedStringSeqs.add(seq);
        }
      });

      if (selectedStringSeqs.size > 1 && selectedStringSeqs.has(hitStringSeq)) {
        return [...selectedStringSeqs]
          .map(seq => resolveStringFeatureBySeq(seq))
          .filter(Boolean);
      }

      const hitString = resolveStringFeatureBySeq(hitStringSeq);
      return hitString ? [hitString] : [];
    },

    /**
     * Ctrl+드래그로 여러 String 놓기 — 각 String을 단일 복사와 동일하게 auto divide 저장
     */
    async processMultiStringCtrlDragDrop(items) {
      if (!items?.length || !this.vectorSource) {
        return;
      }

      logStringCopySave('processMultiStringCtrlDragDrop → start', { count: items.length });

      return this.runWithSiteDesignSaving(async () => {
        const prevAutoDraw = this.autoDrawEnabled;
        this.autoDrawEnabled = true;
        let savedCount = 0;

        try {
          for (let i = 0; i < items.length; i++) {
            const { original, stringClone, moduleClones } = items[i];

            const geom = stringClone.getGeometry()?.clone?.();
            if (!geom) {
              continue;
            }

            const extent = geom.getExtent();
            const dropCenter = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];
            const sourceArraySeq =
              original.get('solarArraySeq') ?? original.get('solarArrayId') ?? null;
            const targetArraySeq =
              this.resolveStringPlacementArraySeqForDrop({
                dropCoordinate: dropCenter,
                sourceFeature: original,
                sourceArraySeq
              }) ?? sourceArraySeq;

            if (targetArraySeq == null) {
              this.warnStringDrawTargetArrayRequired();
              this.vectorSource.removeFeature(stringClone);
              moduleClones?.forEach(mc => this.vectorSource.removeFeature(mc));
              continue;
            }

            const { rowNum, colNum } = this.resolveSourceStringRowCol(original);
            this.applyApiRowColToModuleColRow(rowNum, colNum);

            console.log('[CtrlDragCopy] drop (multi String)', {
              index: i,
              source: {
                stringSeq: readStringSeq(original),
                stringDisplayId: readStringDisplayNo(original),
                arraySeq: sourceArraySeq,
                name: original.get('name') ?? null
              },
              target: { arraySeq: targetArraySeq }
            });

            logStringNumberingInArrayDebug(
              {
                flattened: this.flattenedSiteDesign,
                solarStrings: this.solarStrings,
                arraySeq: targetArraySeq,
                sourceFeature: original
              },
              'CtrlDragCopy → multi dividePolygon (before)'
            );

            const skipGridSlots = this.resolveCtrlDragCopySkipSlots(original);

            this.drawnPolygon = new Feature({ geometry: geom });
            const patch = await this._drawingService.dividePolygon(
              this._drawingContext({
                _ctrlDragCopySourceFeature: original,
                skipGridSlots
              }),
              {
                deferRefresh: true,
                solarArraySeq: targetArraySeq,
                _ctrlDragCopySourceFeature: original,
                skipGridSlots
              }
            );
            if (patch) {
              this.vectorSource.removeFeature(stringClone);
              moduleClones?.forEach(mc => this.vectorSource.removeFeature(mc));
            }
            savedCount += 1;
          }

          if (savedCount > 0) {
            const first = items[0];
            const firstGeom = first?.stringClone?.getGeometry?.();
            if (firstGeom) {
              const ext = firstGeom.getExtent();
              const center = [(ext[0] + ext[2]) / 2, (ext[1] + ext[3]) / 2];
              const seq = this.resolveStringPlacementArraySeqForDrop({
                dropCoordinate: center,
                sourceFeature: first.original
              });
              if (seq != null) {
                this.syncStringDrawTargetFromArraySeq?.(seq);
              }
            }
          }
        } finally {
          this.autoDrawEnabled = prevAutoDraw;
          logStringCopySave('processMultiStringCtrlDragDrop → done', { savedCount });
        }
      });
    },

    /**
     * Ctrl+드래그 String 놓기 → 직사각형 그리기 완료와 동일하게 나누기 설정 팝업까지 진행
     */
    finishStringCtrlDragAsDivision(state) {
      if (!state.clone || !this.vectorSource) {
        this.resetCtrlDragState(state);
        return;
      }

      const geom = state.clone.getGeometry()?.clone?.();
      if (!geom) {
        this.resetCtrlDragState(state);
        return;
      }

      this.vectorSource.removeFeature(state.clone);
      state.moduleClones?.forEach(mc => this.vectorSource.removeFeature(mc));

      const drawnFeature = new Feature({ geometry: geom });
      this.vectorSource.addFeature(drawnFeature);
      this.drawnPolygon = drawnFeature;

      const { rowNum, colNum } = this.resolveSourceStringRowCol(state.original);
      this.applyApiRowColToModuleColRow(rowNum, colNum);

      if (typeof this.processDrawnPolygon === 'function') {
        this.processDrawnPolygon(drawnFeature);
      } else {
        this.showDivisionPopup?.(drawnFeature);
      }

      state.clone = null;
      state.moduleClones = [];
      state.original = null;
      this.teardownCtrlDragTranslate(state, { removeClones: false });
    },

    createCtrlDragModuleClones(sourceString) {
      const stringSeq = readStringSeq(sourceString);
      if (stringSeq == null || !this.vectorSource) {
        return [];
      }
      return this.vectorSource
        .getFeatures()
        .filter(
          f =>
            (f.get('featureType') || '').toLowerCase() === 'module' &&
            readStringSeq(f) === stringSeq
        )
        .map(moduleFeature => {
          const moduleClone = moduleFeature.clone();
          moduleClone.set('isCtrlDragClone', true);
          moduleClone.set('name', `${moduleFeature.get('name') || ''} (복사)`);
          this.vectorSource.addFeature(moduleClone);
          return moduleClone;
        });
    },

    /* ---------------------------------------------------------- */
    /*  Ctrl+드래그 복사 — geometry.translate 방식                 */
    /* ---------------------------------------------------------- */
    setupCtrlDrag() {
      // 중복 등록 방지
      if (this._ctrlDragSetupDone) {
        return;
      }
      this._ctrlDragSetupDone = true;

      let ctrlDragClone = null;
      let ctrlDragStartCoord = null;
      let ctrlDragOriginal = null;
      const viewport = this.map.getViewport();

      viewport.addEventListener(
        'pointerdown',
        event => {
          if (!this.ctrlKeyPressed || event.button !== 0) return;

          const pixel = this.map.getEventPixel(event);
          let hit = this.map.forEachFeatureAtPixel(pixel, f => f, {
            layerFilter: l => l === this.vectorLayer
          });
          if (!hit) return;

          // Module 클릭 → 부모 String으로 승격
          const ft = (hit.get('featureType') || '').toLowerCase();
          if (ft === 'module') {
            const parent = this.vectorSource
              .getFeatures()
              .find(
                f =>
                  (f.get('featureType') || '').toLowerCase() === 'string' &&
                  readStringSeq(f) === readStringSeq(hit)
              );
            if (parent) hit = parent;
          }

          event.preventDefault();
          event.stopPropagation();

          // DragPan 비활성화 — 드래그 중 지도가 움직이지 않도록
          this.dragPanInteraction?.setActive(false);

          ctrlDragOriginal = hit;
          ctrlDragClone = hit.clone();
          ctrlDragClone.set('isCtrlDragClone', true);
          ctrlDragClone.set(
            'name',
            (hit.get('name') || '') + this.$t('panelPlanning.copyNameSuffix')
          );
          this.vectorSource.addFeature(ctrlDragClone);
          ctrlDragStartCoord = this.map.getCoordinateFromPixel(pixel);
        },
        { capture: true }
      );

      viewport.addEventListener(
        'pointermove',
        event => {
          if (!ctrlDragClone) return;
          event.preventDefault();
          event.stopPropagation();
          const coord = this.map.getCoordinateFromPixel(this.map.getEventPixel(event));
          ctrlDragClone
            .getGeometry()
            .translate(coord[0] - ctrlDragStartCoord[0], coord[1] - ctrlDragStartCoord[1]);
          ctrlDragStartCoord = coord;
        },
        { capture: true }
      );

      viewport.addEventListener(
        'pointerup',
        async event => {
          if (!ctrlDragClone || !ctrlDragOriginal) return;
          event.preventDefault();
          event.stopPropagation();

          // DragPan 복구
          this.dragPanInteraction?.setActive(true);

          this.vectorSource.removeFeature(ctrlDragClone);
          const geom = ctrlDragClone.getGeometry();
          const extent = geom.getExtent();
          const center = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];

          const featureType = (ctrlDragOriginal.get('featureType') || '').toLowerCase();
          if (featureType === 'string') {
            const stringSeq = readStringSeq(ctrlDragOriginal);
            const allFeatures = this.vectorSource.getFeatures();
            this.copiedFeature = {
              string: this.extractFeatureData(ctrlDragOriginal),
              modules: allFeatures
                .filter(
                  f =>
                    (f.get('featureType') || '').toLowerCase() === 'module' &&
                    readStringSeq(f) === stringSeq
                )
                .map(m => this.extractFeatureData(m))
            };
            this.copiedFeatureType = 'string';
          } else {
            this.copiedFeature = this.extractFeatureData(ctrlDragOriginal);
            this.copiedFeatureType = 'module';
          }

          await this.pasteFeatureAtCoordinate(center);

          ctrlDragClone = null;
          ctrlDragStartCoord = null;
          ctrlDragOriginal = null;
        },
        { capture: true }
      );

      viewport.addEventListener(
        'pointercancel',
        () => {
          // DragPan 복구
          this.dragPanInteraction?.setActive(true);

          if (ctrlDragClone) {
            this.vectorSource.removeFeature(ctrlDragClone);
          }
          ctrlDragClone = null;
          ctrlDragStartCoord = null;
          ctrlDragOriginal = null;
        },
        { capture: true }
      );
    },

    copySelectedFeature() {
      const groupFeatures = this.groupSelection?.getArray() ?? [];
      const moduleFeatures = this.moduleSelection?.getArray() ?? [];

      if (moduleFeatures.length > 0) {
        this.copiedFeature = this.extractFeatureData(moduleFeatures[0]);
        this.copiedFeatureType = 'module';
        return;
      }

      const stringFeatures = groupFeatures.filter(f => {
        const t = (f.get('featureType') || '').toLowerCase();
        return t === 'string';
      });
      if (stringFeatures.length === 0) {
        console.warn('복사할 피처가 선택되지 않았습니다.');
        return;
      }

      const allFeatures = this.vectorSource.getFeatures();
      const strings = stringFeatures.map(sf => {
        const stringSeq = readStringSeq(sf);
        return {
          string: this.extractFeatureData(sf),
          modules: allFeatures
            .filter(
              f =>
                (f.get('featureType') || '').toLowerCase() === 'module' &&
                readStringSeq(f) === stringSeq
            )
            .map(m => this.extractFeatureData(m))
        };
      });
      this.copiedFeature = strings.length === 1 ? strings[0] : { strings };
      this.copiedFeatureType = strings.length === 1 ? 'string' : 'multistring';
    },

    extractFeatureData(feature) {
      const geometry = feature.getGeometry();
      if (!geometry) return null;
      return {
        name: feature.get('name'),
        featureType: feature.get('featureType'),
        coordinates: geometry.getCoordinates(),
        stringSeq: readStringSeq(feature),
        stringDisplayNo: readStringDisplayNo(feature),
        solarStringId: readStringDisplayNo(feature),
        solarStringSeq: readStringSeq(feature),
        solarModuleId: feature.get('solarModuleId'),
        solarArrayId: feature.get('solarArrayId'),
        solarSectionId: feature.get('solarSectionId'),
        colNum: feature.get('colNum'),
        rowNum: feature.get('rowNum')
      };
    },

    async pasteFeatureAtCoordinate(targetCoordinate) {
      if (!this.copiedFeature || !this.selectedSite) return;
      if (this.copiedFeatureType === 'string' || this.copiedFeatureType === 'multistring') {
        const sourceArraySeq =
          this.copiedFeatureType === 'string'
            ? (this.copiedFeature.string?.solarArraySeq ??
              this.copiedFeature.string?.solarArrayId ??
              null)
            : (this.copiedFeature.strings?.[0]?.string?.solarArraySeq ??
              this.copiedFeature.strings?.[0]?.string?.solarArrayId ??
              null);
        if (
          !this.ensureStringDrawTargetArrayForCopyPaste({
            dropCoordinate: targetCoordinate,
            sourceArraySeq
          })
        ) {
          return;
        }
      }
      this.deactivatePasteMode();
      if (this.copiedFeatureType === 'module') {
        await this.pasteModule(targetCoordinate);
      } else if (this.copiedFeatureType === 'string') {
        await this.pasteString(targetCoordinate);
      } else if (this.copiedFeatureType === 'multistring') {
        await this.pasteMultiString(targetCoordinate);
      }
    },

    async pasteModule(targetCoordinate) {
      const moduleData = this.copiedFeature;
      if (!moduleData?.coordinates) return;
      const originalGeometry = new Polygon(moduleData.coordinates);
      const originalExtent = originalGeometry.getExtent();
      const originalCenter = [
        (originalExtent[0] + originalExtent[2]) / 2,
        (originalExtent[1] + originalExtent[3]) / 2
      ];
      const delta = [
        targetCoordinate[0] - originalCenter[0],
        targetCoordinate[1] - originalCenter[1]
      ];
      const newCoordinates = moduleData.coordinates.map(ring =>
        ring.map(coord => [coord[0] + delta[0], coord[1] + delta[1]])
      );
      const stringSeq = moduleData.stringSeq ?? moduleData.solarStringSeq;
      const stringDisplayNo = moduleData.stringDisplayNo ?? moduleData.solarStringId;
      const modulesInString = (this.vectorSource?.getFeatures?.() ?? []).filter(
        f =>
          (f.get('featureType') || '').toLowerCase() === 'module' &&
          stringSeq != null &&
          readStringSeq(f) === String(stringSeq)
      );
      const maxInString = modulesInString.reduce((max, f) => {
        const id = parseInt(f.get('solarModuleId'), 10);
        return !isNaN(id) && id > max ? id : max;
      }, 0);
      const newModuleId = maxInString + 1;
      const newModuleFeature = new Feature({
        geometry: new Polygon(newCoordinates),
        name: `Module ${newModuleId}`
      });
      newModuleFeature.setId(toPanelMapModuleFeatureId(newModuleId));
      newModuleFeature.set('featureType', 'Module');
      newModuleFeature.set('solarModuleId', newModuleId);
      writeStringIdentityOnFeature(newModuleFeature, {
        seq: stringSeq,
        displayNo: stringDisplayNo
      });
      newModuleFeature.set('solarArrayId', moduleData.solarArrayId);
      newModuleFeature.set('solarSectionId', moduleData.solarSectionId);
      this.vectorSource.addFeature(newModuleFeature);
      this.syncModuleLabelVisibilityAfterFeaturesChange?.();
      this.requestVectorStyleRefresh?.();
    },

    async pasteString(targetCoordinate) {
      await this.pasteStringWithoutLoading(targetCoordinate);
    },

    async pasteStringWithoutLoading(targetCoordinate) {
      return this.runWithSiteDesignSaving(async () => {
      const stringData = this.copiedFeature;
      if (!stringData?.string?.coordinates) {
        console.warn('String 데이터가 유효하지 않습니다.');
        return;
      }

      const originalStringGeometry = new Polygon(stringData.string.coordinates);
      const originalStringExtent = originalStringGeometry.getExtent();
      const originalStringCenter = [
        (originalStringExtent[0] + originalStringExtent[2]) / 2,
        (originalStringExtent[1] + originalStringExtent[3]) / 2
      ];
      const delta = [
        targetCoordinate[0] - originalStringCenter[0],
        targetCoordinate[1] - originalStringCenter[1]
      ];
      const newStringCoordinates = stringData.string.coordinates.map(ring =>
        ring.map(coord => [coord[0] + delta[0], coord[1] + delta[1]])
      );

      const pasteApiRowCol = moduleColRowToApiFields(this.moduleColNum, this.moduleRowNum);
      const sourceArraySeq =
        stringData.string.solarArraySeq ?? stringData.string.solarArrayId ?? null;
      const targetArraySeq =
        this.resolveStringPlacementArraySeqForDrop({
          dropCoordinate: targetCoordinate,
          sourceArraySeq
        }) ?? sourceArraySeq;

      if (targetArraySeq == null) {
        this.warnStringDrawTargetArrayRequired();
        return;
      }

      const newStringId = resolveNextStringDisplayIdInArray({
        flattened: this.flattenedSiteDesign,
        solarStrings: this.solarStrings,
        arraySeq: targetArraySeq
      });
      const newModules = (stringData.modules || []).map((moduleData, index) => {
        const newModuleCoordinates = moduleData.coordinates.map(ring =>
          ring.map(coord => [coord[0] + delta[0], coord[1] + delta[1]])
        );
        return {
          moduleId: index + 1,
          coordinates: newModuleCoordinates,
          originalModuleId: moduleData.solarModuleId
        };
      });

      const solarString = {
        stringId: newStringId,
        name: `String ${newStringId}`,
        shapeCoordinates: this.convToWGS84(newStringCoordinates[0]),
        colNum: stringData.string.colNum ?? pasteApiRowCol.colNum,
        rowNum: stringData.string.rowNum ?? pasteApiRowCol.rowNum,
        solarArrayId: targetArraySeq,
        solarModules: newModules.map(m => ({
          moduleId: m.moduleId,
          shapeCoordinates: this.convToWGS84(m.coordinates[0])
        }))
      };

      try {
        logStringCopySave('pasteStringWithoutLoading → API request', {
          siteId: this.selectedSite?.id,
          targetArraySeq,
          newStringId,
          moduleCount: newModules.length
        });
        const result = await traceStringCopySaveAsync(
          'addSolarStringAndItsModules (paste)',
          () => siteDesignApi.addSolarStringAndItsModules(this.selectedSite.id, solarString)
        );
        logStringCopySave('pasteStringWithoutLoading → API raw summary', summarizeRawApiResult(result));
        this.syncStringDrawTargetFromArraySeq?.(targetArraySeq);
        const applied = await traceStringCopySaveAsync(
          'applyStringCreatedPatch (paste)',
          () =>
            this.applyStringCreatedPatch?.({
              apiResult: result,
              requestPayload: solarString,
              arraySeq: targetArraySeq
            })
        );
        if (applied) {
          logStringCopySave('pasteStringWithoutLoading → 완료', { newStringId });
        } else {
          logStringCopySave('pasteStringWithoutLoading → apply=false', { newStringId });
        }
      } catch (error) {
        console.error('String 붙여넣기 실패:', error);
      }
      });
    },

    async pasteMultiString(targetCoordinate) {
      const strings = this.copiedFeature?.strings;
      if (!strings?.length) return;

      try {
        const firstString = strings[0];
        const origCenter = this.getGeometryCenter(new Polygon(firstString.string.coordinates));
        for (let i = 0; i < strings.length; i++) {
          const sd = strings[i];
          const sc = this.getGeometryCenter(new Polygon(sd.string.coordinates));
          const offset = [
            targetCoordinate[0] - origCenter[0] + (sc[0] - origCenter[0]),
            targetCoordinate[1] - origCenter[1] + (sc[1] - origCenter[1])
          ];
          const saved = this.copiedFeature;
          const savedType = this.copiedFeatureType;
          this.copiedFeature = sd;
          this.copiedFeatureType = 'string';
          await this.pasteStringWithoutLoading(offset);
          this.copiedFeature = saved;
          this.copiedFeatureType = savedType;
        }
      } catch (error) {
        console.error('다중 String 붙여넣기 실패:', error);
      }
    },

    getGeometryCenter(geometry) {
      const extent = geometry.getExtent();
      return [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];
    }
  }
};
